<template>
  <div class="pa-4">
    <v-row dense class="mb-2" align="center">
      <v-col>
        <h3>Atlas Usage</h3>
        <div class="text-caption">
          Third-party AI spend for Atlas (chat, guided workflows, prompt chains), in GBP. The
          client budget and per-user default are set in the Settings tab under "Atlas monthly
          usage budget (GBP)"; individual user budgets are set in the table below.
        </div>
      </v-col>
      <v-col cols="auto" class="d-flex align-center">
        <v-select
          v-model="month"
          :items="monthOptions"
          dense
          outlined
          hide-details
          label="Month"
          style="max-width: 160px"
          class="mr-2"
          @change="loadAll"
        />
        <v-btn small outlined @click="loadAll" :loading="loading">
          <v-icon small left>mdi-refresh</v-icon>Refresh
        </v-btn>
      </v-col>
    </v-row>

    <!-- Headline: spend vs budget -->
    <v-card outlined class="pa-4 mb-4">
      <div class="d-flex align-center flex-wrap">
        <div class="mr-8">
          <div class="text-caption grey--text">Spend ({{ month }})</div>
          <div class="text-h5">{{ formatGbp(summary.spend_gbp) }}</div>
        </div>
        <div class="mr-8">
          <div class="text-caption grey--text">Budget</div>
          <div class="text-h5">
            {{ summary.budget_gbp ? formatGbp(summary.budget_gbp) : 'Unlimited' }}
          </div>
        </div>
        <div class="mr-8">
          <div class="text-caption grey--text">Per-user default</div>
          <div class="text-h5">
            {{
              summary.user_default_budget_gbp
                ? formatGbp(summary.user_default_budget_gbp)
                : 'Unlimited'
            }}
          </div>
        </div>
        <div class="mr-8">
          <div class="text-caption grey--text">API calls</div>
          <div class="text-h5">{{ summary.calls.toLocaleString() }}</div>
        </div>
        <div class="mr-8">
          <div class="text-caption grey--text">Tokens in / out</div>
          <div class="text-h5">
            {{ formatTokens(summary.input_tokens) }} / {{ formatTokens(summary.output_tokens) }}
          </div>
        </div>
      </div>
      <template v-if="summary.budget_gbp">
        <v-progress-linear
          class="mt-3"
          :value="budgetPct"
          height="10"
          rounded
          :color="budgetPct >= 100 ? 'red' : budgetPct >= 80 ? 'amber darken-2' : 'indigo'"
        />
        <div class="text-caption mt-1">
          {{ Math.min(999, Math.round(budgetPct)) }}% of monthly budget used.
          Users see a warning from 80%; Atlas chat pauses at 100%.
        </div>
      </template>
      <div v-if="summary.unpriced_calls > 0" class="text-caption amber--text text--darken-3 mt-2">
        <v-icon x-small color="amber darken-3">mdi-alert</v-icon>
        {{ summary.unpriced_calls }} call(s) used a model with no price configured — their cost is
        NOT included in the spend total above.
      </div>
    </v-card>

    <v-tabs v-model="viewTab" class="mb-3">
      <v-tab>Summary — {{ monthName }}</v-tab>
      <v-tab>Users &amp; Budgets</v-tab>
      <v-tab>Conversations</v-tab>
    </v-tabs>

    <v-tabs-items v-model="viewTab" class="transparent">
      <!-- Summary: charts + workflow/surface breakdowns -->
      <v-tab-item>
        <!-- Charts: by day + by feature -->
        <v-row dense class="mb-4">
          <v-col cols="12" md="8">
        <v-card outlined class="pa-4" style="min-height: 340px">
          <div class="text-subtitle-2 mb-2">Spend by day (GBP)</div>
          <BarChart v-if="byDay.length" :spec="daySpec" />
          <div v-else class="text-caption grey--text">No usage recorded this month.</div>
        </v-card>
      </v-col>
      <v-col cols="12" md="4">
        <v-card outlined class="pa-4" style="min-height: 340px">
          <div class="text-subtitle-2 mb-2">Spend by feature</div>
          <DoughnutChart
            v-if="featureChartData.length"
            :chartData="featureChartData"
            :height="240"
            :showLegend="true"
          />
          <div v-else class="text-caption grey--text">No usage recorded this month.</div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Workflow + surface breakdowns -->
    <v-row dense class="mb-4">
      <v-col cols="12" md="8">
        <v-card outlined>
          <div class="d-flex align-center pa-4 pb-0">
            <div class="text-subtitle-2">Usage by guided workflow</div>
            <v-spacer />
            <v-switch
              v-model="workflowByStep"
              dense
              hide-details
              class="mt-0 pt-0"
              label="Split by step"
              @change="loadWorkflowBreakdown"
            />
          </div>
          <v-data-table
            :headers="workflowHeaders"
            :items="byWorkflow"
            :loading="loading"
            dense
            :items-per-page="10"
            sort-by="cost_gbp"
            sort-desc
            item-key="key"
            show-expand
            :expanded.sync="expandedWorkflows"
            class="elevation-0 row-expand-table"
            no-data-text="No workflow-attributed usage this month"
            @item-expanded="onWorkflowExpanded"
            @click:row="toggleRowExpand"
          >
            <template #[`item.cost_gbp`]="{ item }">{{ formatGbp(item.cost_gbp) }}</template>
            <template #[`item.input_tokens`]="{ item }">{{ formatTokens(item.input_tokens) }}</template>
            <template #[`item.output_tokens`]="{ item }">{{ formatTokens(item.output_tokens) }}</template>
            <template #expanded-item="{ headers, item }">
              <td :colspan="headers.length" class="px-6 py-2">
                <div
                  v-if="!workflowStats[workflowBaseKey(item)] || workflowStats[workflowBaseKey(item)].loading"
                  class="text-caption py-2"
                >
                  Loading workflow stats…
                </div>
                <template v-else-if="workflowItemStats[item.key] && workflowItemStats[item.key].total">
                  <div class="text-caption font-weight-medium mb-1">By path</div>
                  <v-simple-table dense>
                    <thead>
                      <tr>
                        <th>Path</th>
                        <th>Executions</th>
                        <th>Completions</th>
                        <th>Rated</th>
                        <th>Avg rating (rated)</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="p in workflowItemStats[item.key].byPath" :key="p.path">
                        <td>{{ p.path }}</td>
                        <td>{{ p.total }}</td>
                        <td>{{ p.completed }} ({{ p.completedPct }}%)</td>
                        <td>{{ p.rated }} ({{ p.ratedPct }}%)</td>
                        <td>{{ p.avgRating === null ? '—' : p.avgRating }}</td>
                      </tr>
                      <tr class="font-weight-medium">
                        <td>All</td>
                        <td>{{ workflowItemStats[item.key].total }}</td>
                        <td>{{ workflowItemStats[item.key].completed }} ({{ workflowItemStats[item.key].completedPct }}%)</td>
                        <td>{{ workflowItemStats[item.key].rated }} ({{ workflowItemStats[item.key].ratedPct }}%)</td>
                        <td>{{ workflowItemStats[item.key].avgRating === null ? '—' : workflowItemStats[item.key].avgRating }}</td>
                      </tr>
                    </tbody>
                  </v-simple-table>
                  <div class="text-caption font-weight-medium mt-3 mb-1">By context (page / surface)</div>
                  <v-simple-table dense>
                    <thead>
                      <tr>
                        <th>Context</th>
                        <th>Executions</th>
                        <th>Completions</th>
                        <th>Rated</th>
                        <th>Avg rating (rated)</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="c in workflowItemStats[item.key].byContext" :key="c.path">
                        <td>{{ c.path }}</td>
                        <td>{{ c.total }}</td>
                        <td>{{ c.completed }} ({{ c.completedPct }}%)</td>
                        <td>{{ c.rated }} ({{ c.ratedPct }}%)</td>
                        <td>{{ c.avgRating === null ? '—' : c.avgRating }}</td>
                      </tr>
                    </tbody>
                  </v-simple-table>
                  <div class="text-caption grey--text mt-2">
                    {{ statsScopeCaption(item) }}
                  </div>
                </template>
                <div v-else class="text-caption py-2">
                  {{ noStatsCaption(item) }}
                </div>
              </td>
            </template>
          </v-data-table>
          <div class="text-caption grey--text px-4 pb-3">
            Attribution covers Atlas chat turns driven by workflow cards (and turns where a
            workflow tool ran). Everything else is in "Usage outside workflows" below. Expand a
            row for the workflow's execution stats (paths, completions, ratings).
          </div>
        </v-card>
      </v-col>
      <v-col cols="12" md="4">
        <v-card outlined>
          <div class="text-subtitle-2 pa-4 pb-0">Usage by surface</div>
          <v-data-table
            :headers="surfaceHeaders"
            :items="bySurface"
            :loading="loading"
            dense
            hide-default-footer
            sort-by="cost_gbp"
            sort-desc
            class="elevation-0"
            no-data-text="No usage recorded this month"
          >
            <template #[`item.label`]="{ item }">
              {{ SURFACE_LABELS[item.label] || item.label }}
            </template>
            <template #[`item.cost_gbp`]="{ item }">{{ formatGbp(item.cost_gbp) }}</template>
          </v-data-table>
          <div class="text-caption grey--text px-4 pb-3">
            In-app = portal chat. MCP = Claude/Copilot connectors calling Atlas server-side
            (ask_atlas and workflow tools). Untagged = background work (chains, narrations).
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Non-workflow usage, by what drove it -->
    <v-row dense class="mb-4">
      <v-col cols="12" md="8">
        <v-card outlined>
          <div class="text-subtitle-2 pa-4 pb-0">Usage outside workflows</div>
          <v-data-table
            :headers="contextHeaders"
            :items="byContext"
            :loading="loading"
            dense
            :items-per-page="10"
            sort-by="cost_gbp"
            sort-desc
            class="elevation-0"
            no-data-text="No non-workflow usage this month"
          >
            <template #[`item.label`]="{ item }">{{ contextLabel(item.key) }}</template>
            <template #[`item.cost_gbp`]="{ item }">{{ formatGbp(item.cost_gbp) }}</template>
            <template #[`item.input_tokens`]="{ item }">{{ formatTokens(item.input_tokens) }}</template>
            <template #[`item.output_tokens`]="{ item }">{{ formatTokens(item.output_tokens) }}</template>
          </v-data-table>
          <div class="text-caption grey--text px-4 pb-3">
            Everything not attributed to a guided workflow: free-form chat split by the page it
            ran against, plus background work (chain narrations, prompt chains, title
            suggestions). Chat turns recorded before page-context tracking was added show under
            "Chat — general".
          </div>
        </v-card>
      </v-col>
    </v-row>
      </v-tab-item>

      <!-- Users & budgets -->
      <v-tab-item>
    <!-- By user: spend + editable budgets -->
    <v-card outlined class="mb-4">
      <div class="d-flex align-center pa-4 pb-0">
        <div class="text-subtitle-2">Usage &amp; budgets by user</div>
        <v-spacer />
        <v-text-field
          v-model="userSearch"
          dense
          outlined
          hide-details
          clearable
          label="Find user"
          prepend-inner-icon="mdi-magnify"
          style="max-width: 240px"
        />
      </div>
      <v-data-table
        :headers="userHeaders"
        :items="userBudgetRows"
        :search="userSearch"
        :loading="loading"
        dense
        :items-per-page="20"
        sort-by="spend_gbp"
        sort-desc
        item-key="user_id"
        show-expand
        :expanded.sync="expandedUsers"
        class="elevation-0 row-expand-table"
        @item-expanded="onUserExpanded"
        @click:row="toggleRowExpand"
      >
        <template #[`item.spend_gbp`]="{ item }">
          {{ formatGbp(item.spend_gbp) }}
        </template>
        <template #[`item.input_tokens`]="{ item }">
          {{ formatTokens(item.input_tokens) }}
        </template>
        <template #[`item.output_tokens`]="{ item }">
          {{ formatTokens(item.output_tokens) }}
        </template>
        <template #[`item.budget`]="{ item }">
          <span v-if="item.monthly_budget_gbp === null" class="grey--text">
            {{
              summary.user_default_budget_gbp
                ? `${formatGbp(summary.user_default_budget_gbp)} (default)`
                : 'Unlimited (default)'
            }}
          </span>
          <span v-else-if="item.monthly_budget_gbp === 0">Unlimited</span>
          <span v-else>{{ formatGbp(item.monthly_budget_gbp) }}</span>
        </template>
        <template #[`item.actions`]="{ item }">
          <v-btn icon small @click.stop="openBudgetDialog(item)" title="Set this user's budget">
            <v-icon small>mdi-pencil</v-icon>
          </v-btn>
        </template>
        <template #expanded-item="{ headers, item }">
          <td :colspan="headers.length" class="px-6 py-2">
            <div
              v-if="!userConversations[item.user_id] || userConversations[item.user_id].loading"
              class="text-caption py-2"
            >
              Loading conversations…
            </div>
            <v-simple-table v-else-if="userConversations[item.user_id].rows.length" dense>
              <thead>
                <tr>
                  <th class="text-left">Conversation</th>
                  <th class="text-right">Conversations</th>
                  <th class="text-right">Cost (GBP)</th>
                  <th class="text-right">Calls</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in userConversations[item.user_id].rows" :key="row.label">
                  <td>{{ row.label }}</td>
                  <td class="text-right">{{ row.conversations }}</td>
                  <td class="text-right">{{ formatGbp(row.cost_gbp) }}</td>
                  <td class="text-right">{{ row.calls }}</td>
                </tr>
              </tbody>
            </v-simple-table>
            <div v-else class="text-caption py-2">
              No conversation-linked usage for this user this month.
            </div>
          </td>
        </template>
      </v-data-table>
      <div class="text-caption grey--text px-4 pb-3">
        Spend is for the selected month; budgets always apply to the current month. A user with no
        individual budget follows the per-user default. Background work (chains, narrations) isn't
        tied to a user, so these rows may not sum to the client total.
      </div>
    </v-card>

    <!-- Per-user budget editor -->
    <v-dialog v-model="budgetDialog.open" max-width="440">
      <v-card v-if="budgetDialog.user">
        <v-card-title class="text-subtitle-1">
          Monthly Atlas budget — {{ budgetDialog.user.name }}
        </v-card-title>
        <v-card-text>
          <v-radio-group v-model="budgetDialog.mode" hide-details class="mt-0">
            <v-radio value="default">
              <template #label>
                Use the per-user default
                <span class="grey--text ml-1">
                  ({{
                    summary.user_default_budget_gbp
                      ? formatGbp(summary.user_default_budget_gbp)
                      : 'unlimited'
                  }})
                </span>
              </template>
            </v-radio>
            <v-radio value="unlimited" label="Unlimited for this user" />
            <v-radio value="custom" label="Custom monthly cap" />
          </v-radio-group>
          <v-text-field
            v-if="budgetDialog.mode === 'custom'"
            v-model.number="budgetDialog.amount"
            class="mt-3"
            dense
            outlined
            hide-details
            type="number"
            min="0"
            label="Cap (GBP per month)"
            prefix="£"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="budgetDialog.open = false">Cancel</v-btn>
          <v-btn
            color="primary"
            :loading="budgetDialog.saving"
            :disabled="budgetDialog.mode === 'custom' && !(budgetDialog.amount > 0)"
            @click="saveBudget"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
      </v-tab-item>

      <!-- Top conversations -->
      <v-tab-item>
    <v-card outlined>
      <div class="text-subtitle-2 pa-4 pb-0">Conversation spend by title</div>
      <v-data-table
        :headers="conversationHeaders"
        :items="byConversation"
        :loading="loading"
        dense
        :items-per-page="15"
        sort-by="cost_gbp"
        sort-desc
        item-key="label"
        show-expand
        :expanded.sync="expandedConversations"
        class="elevation-0 row-expand-table"
        no-data-text="No conversation-linked usage this month"
        @item-expanded="onConversationExpanded"
        @click:row="toggleRowExpand"
      >
        <template #[`item.cost_gbp`]="{ item }">{{ formatGbp(item.cost_gbp) }}</template>
        <template #[`item.input_tokens`]="{ item }">{{ formatTokens(item.input_tokens) }}</template>
        <template #[`item.output_tokens`]="{ item }">{{ formatTokens(item.output_tokens) }}</template>
        <template #expanded-item="{ headers, item }">
          <td :colspan="headers.length" class="px-6 py-2">
            <div
              v-if="!conversationUsers[item.label] || conversationUsers[item.label].loading"
              class="text-caption py-2"
            >
              Loading users…
            </div>
            <v-simple-table v-else-if="conversationUsers[item.label].rows.length" dense>
              <thead>
                <tr>
                  <th class="text-left">User</th>
                  <th class="text-right">Conversations</th>
                  <th class="text-right">Cost (GBP)</th>
                  <th class="text-right">Calls</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in conversationUsers[item.label].rows" :key="row.user_id || row.label">
                  <td>{{ row.label }}</td>
                  <td class="text-right">{{ row.conversations }}</td>
                  <td class="text-right">{{ formatGbp(row.cost_gbp) }}</td>
                  <td class="text-right">{{ row.calls }}</td>
                </tr>
              </tbody>
            </v-simple-table>
            <div v-else class="text-caption py-2">No users found for this grouping.</div>
          </td>
        </template>
      </v-data-table>
      <div class="text-caption grey--text px-4 pb-3">
        Conversations sharing a title (e.g. re-runs of the same workflow) are rolled up into one
        row. Long conversations cost more per turn — the whole history is re-processed on every
        call. Chains and other background work aren't tied to a conversation and are excluded here.
      </div>
    </v-card>
      </v-tab-item>
    </v-tabs-items>
  </div>
</template>

<script>
import axios from 'axios';
import BarChart from '../agent/charts/BarChart.vue';
import DoughnutChart from '../common/DoughnutChart.vue';
import { computeInstanceStats } from './workflowInstanceStats';

const FEATURE_LABELS = {
  chat: 'Atlas chat',
  chat_narration: 'Chain narration',
  title_suggestion: 'Title suggestions',
  prompt_chain: 'Prompt chains',
  workflow_generate: 'Document drafting',
};

const SURFACE_LABELS = {
  portal: 'In-app (portal)',
  'mcp-app': 'MCP (app cards)',
  'mcp-text': 'MCP (text)',
  '(untagged)': 'Background / untagged',
};

// Keys from the by=context breakdown: chat rows are 'chat:<page_context
// kind>' (with '(general)'/'(mcp)' fallbacks for untagged turns), other
// features key as themselves. Unknown chat kinds fall through to
// "Chat — <kind>" so new page surfaces label themselves.
const CONTEXT_LABELS = {
  'chat:document': 'Chat — document page',
  'chat:skills': 'Chat — skills page',
  'chat:documents.grid': 'Chat — jobs grid',
  'chat:(general)': 'Chat — general',
  'chat:(mcp)': 'Chat — MCP',
  chat_narration: 'Chain narration',
  title_suggestion: 'Title suggestions',
  prompt_chain: 'Prompt chains',
  // Drafting calls now attribute to their workflow (and appear in the
  // workflow table); this label only surfaces for rows recorded before
  // that attribution existed.
  workflow_generate: 'Document drafting (guided workflows)',
};

const FEATURE_COLOURS = ['#2d99ff', '#2cd9c5', '#FF9020', '#BC80BD', '#FF4069', '#D9D9D9'];

export default {
  name: 'cAdminAtlasUsage',
  components: { BarChart, DoughnutChart },
  data() {
    return {
      SURFACE_LABELS,
      loading: false,
      month: new Date().toISOString().slice(0, 7),
      workflowByStep: false,
      viewTab: 0,
      userSearch: '',
      expandedUsers: [],
      expandedConversations: [],
      expandedWorkflows: [],
      // Lazy per-row expansions, keyed by user_id / conversation title /
      // workflow key: { loading, rows|stats }. Populated via $set (Vue 2
      // can't observe new keys).
      userConversations: {},
      conversationUsers: {},
      workflowStats: {},
      budgetDialog: {
        open: false,
        user: null,
        mode: 'default',
        amount: null,
        saving: false,
      },
      summary: {
        budget_gbp: null,
        user_default_budget_gbp: null,
        spend_gbp: 0,
        calls: 0,
        input_tokens: 0,
        output_tokens: 0,
        unpriced_calls: 0,
        by_feature: [],
      },
      byDay: [],
      byUser: [],
      userBudgets: [],
      byWorkflow: [],
      bySurface: [],
      byContext: [],
      byConversation: [],
      userHeaders: [
        { text: 'User', value: 'name' },
        { text: 'Spend (GBP)', value: 'spend_gbp', align: 'end' },
        { text: 'Calls', value: 'calls', align: 'end' },
        { text: 'Tokens in', value: 'input_tokens', align: 'end' },
        { text: 'Tokens out', value: 'output_tokens', align: 'end' },
        { text: 'Budget (GBP)', value: 'budget', align: 'end', sortable: false },
        { text: '', value: 'actions', align: 'end', sortable: false, filterable: false },
      ],
      surfaceHeaders: [
        { text: 'Surface', value: 'label' },
        { text: 'Cost (GBP)', value: 'cost_gbp', align: 'end' },
        { text: 'Calls', value: 'calls', align: 'end' },
      ],
      contextHeaders: [
        { text: 'Context', value: 'label' },
        { text: 'Cost (GBP)', value: 'cost_gbp', align: 'end' },
        { text: 'Calls', value: 'calls', align: 'end' },
        { text: 'Tokens in', value: 'input_tokens', align: 'end' },
        { text: 'Tokens out', value: 'output_tokens', align: 'end' },
      ],
      conversationHeaders: [
        { text: 'Conversation', value: 'label' },
        { text: 'Conversations', value: 'conversations', align: 'end' },
        { text: 'Cost (GBP)', value: 'cost_gbp', align: 'end' },
        { text: 'Calls', value: 'calls', align: 'end' },
        { text: 'Tokens in', value: 'input_tokens', align: 'end' },
        { text: 'Tokens out', value: 'output_tokens', align: 'end' },
      ],
    };
  },
  computed: {
    workflowHeaders() {
      return [
        { text: this.workflowByStep ? 'Workflow · step' : 'Workflow', value: 'label' },
        { text: 'Cost (GBP)', value: 'cost_gbp', align: 'end' },
        { text: 'Calls', value: 'calls', align: 'end' },
        { text: 'Tokens in', value: 'input_tokens', align: 'end' },
        { text: 'Tokens out', value: 'output_tokens', align: 'end' },
      ];
    },
    monthOptions() {
      // Current month + 11 previous, newest first.
      const options = [];
      const now = new Date();
      for (let i = 0; i < 12; i++) {
        const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1));
        options.push(d.toISOString().slice(0, 7));
      }
      return options;
    },
    monthName() {
      const [year, month] = this.month.split('-').map(Number);
      return new Date(Date.UTC(year, month - 1, 1)).toLocaleString('default', {
        month: 'long',
        timeZone: 'UTC',
      });
    },
    budgetPct() {
      if (!this.summary.budget_gbp) return 0;
      return (this.summary.spend_gbp / this.summary.budget_gbp) * 100;
    },
    daySpec() {
      // Fill every day of the selected month so gaps show as zero.
      const [year, month] = this.month.split('-').map(Number);
      const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();
      const costByDay = new Map(
        this.byDay.map((d) => [d.key.slice(8), d.cost_gbp]),
      );
      const data = [];
      for (let day = 1; day <= daysInMonth; day++) {
        const label = String(day).padStart(2, '0');
        data.push({
          label,
          value: Math.round((costByDay.get(label) || 0) * 100) / 100,
        });
      }
      return {
        title: '',
        orientation: 'vertical',
        data,
        color: 'rgba(99, 102, 241, 0.75)',
        x_label: 'Day of month',
        y_label: 'GBP',
      };
    },
    featureChartData() {
      return (this.summary.by_feature || []).map((f, i) => ({
        label: FEATURE_LABELS[f.feature] || f.feature,
        value: Math.round(f.cost_gbp * 100) / 100,
        colour: FEATURE_COLOURS[i % FEATURE_COLOURS.length],
      }));
    },
    // Per-ROW execution stats for the expanded rows, filtered to
    // executions STARTED in the selected month so they line up with the
    // month-scoped spend figures beside them — and, in split-by-step
    // mode, to executions that TOUCHED that row's step (each instance
    // row carries its touched-step set). The '(no step)' bucket has no
    // step to filter on and shows the whole workflow. Recomputes when
    // the month changes; the fetched rows are month-independent.
    workflowItemStats() {
      const out = {};
      for (const item of this.byWorkflow || []) {
        const entry = this.workflowStats[this.workflowBaseKey(item)];
        if (!entry || entry.loading) continue;
        // created is an ISO-ish datetime string — the YYYY-MM prefix
        // comparison avoids Date parsing entirely.
        const monthRows = (entry.rows || []).filter((r) =>
          String(r.created || '').startsWith(this.month),
        );
        const step = this.workflowStepKey(item);
        const rows = step
          ? monthRows.filter((r) => Array.isArray(r.steps) && r.steps.includes(step))
          : monthRows;
        out[item.key] = computeInstanceStats(rows);
      }
      return out;
    },
    // user-budgets rows (every active user + spend + budget override)
    // enriched with token counts from the by-user breakdown.
    userBudgetRows() {
      const tokensByUser = new Map(
        (this.byUser || []).map((u) => [u.key, u]),
      );
      return (this.userBudgets || []).map((u) => {
        const usage = tokensByUser.get(u.user_id) || {};
        return {
          ...u,
          input_tokens: Number(usage.input_tokens || 0),
          output_tokens: Number(usage.output_tokens || 0),
        };
      });
    },
  },
  created() {
    this.loadAll();
  },
  methods: {
    async loadAll() {
      this.loading = true;
      // Expansions belong to the previous month's data — collapse and refetch.
      // (Workflow stats are month-independent, so their cache survives;
      // the rows still collapse because their item keys may change.)
      this.expandedUsers = [];
      this.expandedConversations = [];
      this.expandedWorkflows = [];
      this.userConversations = {};
      this.conversationUsers = {};
      try {
        const [
          summaryResp,
          dayResp,
          userResp,
          userBudgetsResp,
          workflowResp,
          surfaceResp,
          contextResp,
          conversationResp,
        ] = await Promise.all([
          axios.get(`agent/usage/admin/summary?month=${this.month}`),
          axios.get(`agent/usage/admin/breakdown?month=${this.month}&by=day`),
          axios.get(`agent/usage/admin/breakdown?month=${this.month}&by=user`),
          axios.get(`agent/usage/admin/user-budgets?month=${this.month}`),
          axios.get(
            `agent/usage/admin/breakdown?month=${this.month}&by=${this.workflowByStep ? 'workflow_step' : 'workflow'}`,
          ),
          axios.get(`agent/usage/admin/breakdown?month=${this.month}&by=surface`),
          axios.get(`agent/usage/admin/breakdown?month=${this.month}&by=context`),
          axios.get(`agent/usage/admin/breakdown?month=${this.month}&by=conversation_title`),
        ]);
        this.summary = summaryResp.data.Data;
        // Chart wants chronological order; the API sorts by cost.
        this.byDay = (dayResp.data.Data || []).slice().sort((a, b) => a.key.localeCompare(b.key));
        this.byUser = userResp.data.Data || [];
        this.userBudgets = userBudgetsResp.data.Data || [];
        this.byWorkflow = workflowResp.data.Data || [];
        this.bySurface = surfaceResp.data.Data || [];
        this.byContext = contextResp.data.Data || [];
        this.byConversation = conversationResp.data.Data || [];
      } catch (err) {
        console.error('Failed to load Atlas usage', err);
      } finally {
        this.loading = false;
      }
    },
    async loadWorkflowBreakdown() {
      // Item keys change shape between workflow and workflow-step rows —
      // collapse; the per-workflow stats cache stays valid either way.
      this.expandedWorkflows = [];
      try {
        const resp = await axios.get(
          `agent/usage/admin/breakdown?month=${this.month}&by=${this.workflowByStep ? 'workflow_step' : 'workflow'}`,
        );
        this.byWorkflow = resp.data.Data || [];
      } catch (err) {
        console.error('Failed to load workflow usage breakdown', err);
      }
    },
    // Step rows key as '<workflow_key> · <step_key>' (both slugs, so
    // ' · ' can only be the separator) — instance fetches are per workflow.
    workflowBaseKey(item) {
      return String(item.key || '').split(' · ')[0];
    },
    // The step portion of a split row's key, or null when there is
    // nothing to filter on (plain rows, and the '(no step)' bucket —
    // spend that isn't attributed to a specific step).
    workflowStepKey(item) {
      if (!this.workflowByStep) return null;
      const [, ...rest] = String(item.key || '').split(' · ');
      const step = rest.join(' · ');
      return step && step !== '(no step)' ? step : null;
    },
    statsScopeCaption(item) {
      const step = this.workflowStepKey(item);
      if (step) return `Executions started in ${this.monthName} that touched the "${step}" step.`;
      if (this.workflowByStep) {
        return `Executions started in ${this.monthName} — this row's spend isn't attributed to a step, so all executions are shown.`;
      }
      return `Executions started in ${this.monthName}.`;
    },
    noStatsCaption(item) {
      const step = this.workflowStepKey(item);
      return step
        ? `No ${this.monthName} executions touched the "${step}" step.`
        : `No executions recorded for this workflow in ${this.monthName}.`;
    },
    async onWorkflowExpanded({ item, value }) {
      const key = this.workflowBaseKey(item);
      if (!value || !key || this.workflowStats[key]) return;
      // Raw rows are cached (they're month-independent); the
      // workflowMonthStats computed filters them to the selected month.
      this.$set(this.workflowStats, key, { loading: true, rows: [] });
      try {
        const resp = await axios.get(
          `agent/workflows/admin/instances/${encodeURIComponent(key)}`,
        );
        const rows = resp.data.Status === 'OK' ? resp.data.Data || [] : [];
        this.$set(this.workflowStats, key, { loading: false, rows });
      } catch (err) {
        console.error('Failed to load workflow execution stats', err);
        this.$set(this.workflowStats, key, { loading: false, rows: [] });
      }
    },
    // @click:row passes (item, slotData) — slotData carries the expand
    // toggle, and expand() fires item-expanded so lazy loading still runs.
    toggleRowExpand(item, slot) {
      slot.expand(!slot.isExpanded);
    },
    async onUserExpanded({ item, value }) {
      if (!value || this.userConversations[item.user_id]) return;
      this.$set(this.userConversations, item.user_id, { loading: true, rows: [] });
      try {
        const resp = await axios.get(
          `agent/usage/admin/breakdown?month=${this.month}&by=conversation_title&user_id=${encodeURIComponent(item.user_id)}`,
        );
        this.$set(this.userConversations, item.user_id, {
          loading: false,
          rows: resp.data.Data || [],
        });
      } catch (err) {
        console.error('Failed to load user conversation breakdown', err);
        this.$set(this.userConversations, item.user_id, { loading: false, rows: [] });
      }
    },
    async onConversationExpanded({ item, value }) {
      if (!value || this.conversationUsers[item.label]) return;
      this.$set(this.conversationUsers, item.label, { loading: true, rows: [] });
      try {
        const resp = await axios.get(
          `agent/usage/admin/breakdown?month=${this.month}&by=conversation_title_users&title=${encodeURIComponent(item.label)}`,
        );
        this.$set(this.conversationUsers, item.label, {
          loading: false,
          rows: resp.data.Data || [],
        });
      } catch (err) {
        console.error('Failed to load conversation users breakdown', err);
        this.$set(this.conversationUsers, item.label, { loading: false, rows: [] });
      }
    },
    openBudgetDialog(item) {
      this.budgetDialog = {
        open: true,
        user: item,
        mode:
          item.monthly_budget_gbp === null
            ? 'default'
            : item.monthly_budget_gbp === 0
              ? 'unlimited'
              : 'custom',
        amount: item.monthly_budget_gbp > 0 ? item.monthly_budget_gbp : null,
        saving: false,
      };
    },
    async saveBudget() {
      const d = this.budgetDialog;
      const budget =
        d.mode === 'default' ? null : d.mode === 'unlimited' ? 0 : Number(d.amount);
      d.saving = true;
      try {
        await axios.post('agent/usage/admin/user-budget', {
          user_id: d.user.user_id,
          monthly_budget_gbp: budget,
        });
        const row = this.userBudgets.find((u) => u.user_id === d.user.user_id);
        if (row) row.monthly_budget_gbp = budget;
        d.open = false;
      } catch (err) {
        console.error('Failed to save user budget', err);
      } finally {
        d.saving = false;
      }
    },
    // Known keys map to friendly labels; unknown chat contexts (a new
    // page surface) self-label as "Chat — <kind>".
    contextLabel(key) {
      if (CONTEXT_LABELS[key]) return CONTEXT_LABELS[key];
      if (String(key).startsWith('chat:')) return `Chat — ${String(key).slice(5)}`;
      return key;
    },
    formatGbp(value) {
      return `£${Number(value || 0).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    },
    formatTokens(value) {
      const n = Number(value || 0);
      if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
      if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
      return String(n);
    },
  },
};
</script>

<style scoped>
/* Whole row toggles expansion — signal it. Expanded detail rows (and the
   nested tables inside them) keep the default cursor. */
.row-expand-table ::v-deep tbody > tr:not(.v-data-table__expanded__content) {
  cursor: pointer;
}
.row-expand-table ::v-deep tbody > tr:not(.v-data-table__expanded__content) tbody tr {
  cursor: default;
}
</style>
